<template>
  <div class="ds">
    <header class="ds-head">
      <div class="ds-wrap">
        <p class="ds-eyebrow text-uppercase mb-3">Design system · {{ scope }}</p>
        <h1 class="mb-3">Componenti<br />e token</h1>
        <p class="ds-lede mb-3">
          Ogni elemento è renderizzato dai componenti reali con il tema compilato.
          Questa è l'intera palette: {{ tokenCount }} colori, non uno di più.
        </p>
        <div class="d-flex flex-wrap ds-tags">
          <span class="badge badge-light">{{ tokenCount }} colori, tutta la palette</span>
          <span class="badge badge-light">tema {{ scope }}</span>
          <span class="badge badge-light">font {{ fontFamily }}</span>
          <span class="badge badge-light">raggio base {{ radius }}</span>
        </div>
      </div>
    </header>

    <div class="ds-wrap ds-body">
      <nav class="ds-nav">
        <a v-for="s in sections" :key="s.id" :href="'#' + s.id">{{ s.label }}</a>
      </nav>

      <main class="ds-main">
        <!-- PRIMITIVE -->
        <section id="primitive">
          <h2>Primitive</h2>
          <p class="ds-note">
            L'intera palette in <strong>{{ primitives.length }} valori</strong>. Non ce ne
            sono altri: tutto il resto del tema è derivato da questi, per alias o con
            l'alfa. I due colori di marchio sono invariati.
          </p>
          <div class="ds-grid">
            <article v-for="p in primitives" :key="p.v" class="ds-chip shadowedCard">
              <div class="ds-chip__sw" :class="'ds-prim--' + p.v" :data-token="p.v"></div>
              <div class="ds-chip__meta">
                <span class="ds-chip__name">${{ p.v }}</span>
                <span class="ds-chip__val">{{ resolved[p.v] || '—' }}</span>
                <span class="ds-chip__foot">
                  <span :class="crClass(p.v)">{{ crLabel(p.v) }}</span>
                  <span class="ds-chip__use">{{ p.role }}</span>
                </span>
              </div>
            </article>
          </div>
        </section>

        <!-- GLASS -->
        <section id="glass">
          <h2>Superfici traslucide</h2>
          <p class="ds-note">
            La pagina ha un'aura di marca in alto (<code>_chrome.scss</code> §10) e le
            superfici sono vetro: card e campi lasciano passare l'aura (bianco all'80–85%,
            senza sfocatura), modali e bottoni <code>white</code> sfocano davvero ciò che
            hanno dietro (<code>backdrop-filter: blur($glass-blur)</code>). Qui il campione
            su un fondo pieno, dove la sfocatura si vede.
          </p>
          <div class="ds-glass-stage">
            <div class="ds-glass">
              <strong>Pannello in vetro</strong>
              <p class="mb-0"><small>$glass-surface · blur $glass-blur · bordo $glass-border</small></p>
            </div>
          </div>
        </section>

        <!-- TIPOGRAFIA -->
        <section id="tipografia">
          <h2>Tipografia</h2>
          <p class="ds-note">
            Font <strong>{{ fontFamily }}</strong>, variabile: l'asse dei pesi è continuo
            da 200 a 800, quindi ogni peso è reale e nessuno viene sintetizzato dal browser.
            Titoli a 700 (<code>$headings-font-weight</code>), testo a 400.
          </p>
          <div class="shadowedCard p-4">
            <p class="ds-t0 mb-2">Presa in carico del paziente</p>
            <p class="ds-t1 mb-2">Presa in carico del paziente</p>
            <p class="ds-t2 mb-2">Presa in carico del paziente</p>
            <p class="ds-t3 mb-3">Presa in carico del paziente</p>
            <p class="mb-2">Testo corrente al valore base, con l'interlinea dell'interfaccia.</p>
            <p class="text-muted mb-0"><small>Testo secondario, taglia ridotta.</small></p>
          </div>
          <div class="shadowedCard p-4 mt-3">
            <h3>Asse dei pesi</h3>
            <div class="ds-weights">
              <div v-for="w in weights" :key="w" class="ds-weights__item">
                <span class="ds-weights__sample" :style="{ fontWeight: w }">Aa</span>
                <small class="d-block">{{ w }}</small>
              </div>
            </div>
          </div>
        </section>

        <!-- BOTTONI -->
        <section id="bottoni">
          <h2>Bottoni</h2>
          <p class="ds-note">
            Sempre <code>&lt;b-button variant="…"&gt;</code>: la pelle è in
            <code>_buttons.scss</code>, i template non cambiano. Pillola in tutte le taglie, forma ripresa da Oura: alto 48px con 12/24px di padding, 16px peso 700 (sm 42px, lg 56px), nessun tracking. Da tastiera il focus è un contorno
            scuro di 2px staccato dal bordo; al click del mouse non compare nulla.
          </p>
          <div class="shadowedCard p-4">
            <h3>Gerarchia</h3>
            <div class="ds-row align-items-center mb-3">
              <b-button variant="secondary">Conferma</b-button>
              <b-button variant="primary">Prenota</b-button>
              <b-button variant="popup-button">Annulla</b-button>
              <b-button variant="white">Dettagli</b-button>
              <b-button variant="mdm-light-grey">Filtri</b-button>
              <b-button variant="link">Mostra tutto</b-button>
            </div>
            <ul class="ds-legend mb-4">
              <li><code>secondary</code> — azione principale, una per schermata: il navy della navigazione (bianco 14,7:1)</li>
              <li><code>primary</code> — accento: teal profondo con testo bianco (5,7:1); sul teal chiaro il bianco è 3,3:1</li>
              <li><code>popup-button</code> — tonale, per OK e Annulla nelle modali</li>
              <li><code>white</code> — vetro: bianco sul bianco, traslucido e sfocato sul colore</li>
              <li><code>mdm-light-grey</code> — neutro, per azioni di contorno</li>
              <li><code>link</code> — solo testo, si sottolinea in hover</li>
            </ul>

            <h3>Semantiche e contorno</h3>
            <div class="ds-row align-items-center mb-4">
              <b-button variant="success">Salvato</b-button>
              <b-button variant="danger">Elimina</b-button>
              <b-button variant="warning">Attenzione</b-button>
              <b-button variant="outline-secondary">Contorno</b-button>
              <b-button variant="outline-primary">Contorno accento</b-button>
              <b-button variant="outline-danger">Rimuovi</b-button>
            </div>

            <h3>Taglie e stati</h3>
            <div class="ds-row align-items-center mb-3">
              <b-button variant="secondary" size="sm">Piccolo · 34</b-button>
              <b-button variant="secondary">Normale · 40</b-button>
              <b-button variant="secondary" size="lg">Grande · 50</b-button>
              <b-button variant="secondary" disabled>Disabilitato</b-button>
              <b-button variant="secondary"><b-spinner small class="mr-2" />In corso</b-button>
              <b-button variant="secondary"><b-icon icon="plus-lg" class="mr-2" aria-hidden="true" />Con icona</b-button>
            </div>
            <b-button variant="secondary" block class="mb-3">A tutta larghezza (block)</b-button>
            <p class="ds-hint mb-0">Premi <kbd>Tab</kbd> per vedere il contorno di focus: con il mouse non compare.</p>
          </div>

          <h3 class="mt-4">Sui fondi colorati</h3>
          <p class="ds-note">
            Il bottone <code>white</code> e i campi sono traslucidi: sul colore o su
            un'immagine diventano vetro, sul bianco restano bianchi. I campi non
            sfocano lo sfondo, i bottoni sì.
          </p>
          <div class="ds-glass-stage ds-glass-stage--controls">
            <div class="ds-row align-items-center">
              <b-button variant="white">Vetro</b-button>
              <b-button variant="primary">Accento</b-button>
              <b-form-input placeholder="Cerca un servizio" class="ds-w-auto" aria-label="Campo su fondo colorato" />
            </div>
          </div>
        </section>

        <!-- FORM -->
        <section id="form">
          <h2>Form e input</h2>
          <p class="ds-note">
            Sempre <code>b-form-group</code> con <code>b-form-input</code>,
            <code>-select</code>, <code>-checkbox</code>…: la pelle è in
            <code>_forms.scss</code>. Campo alto 48px come i bottoni, raggio 12px, bordo grigio a
            4,6:1, superficie traslucida. In focus il bordo diventa scuro, con un
            alone appena percettibile: si vede, senza blu. L'errore usa colore, icona
            e testo insieme.
          </p>
          <div class="shadowedCard p-4">
            <b-row>
              <b-col md="6">
                <b-form-group label="Nome e cognome" label-for="ds-nome" description="Come compare sulla tessera sanitaria">
                  <b-form-input id="ds-nome" placeholder="Maria Rossi" />
                </b-form-group>
                <b-form-group label="Codice fiscale (sola lettura)" label-for="ds-cf">
                  <b-form-input id="ds-cf" value="RSSMRA80A41H501Z" readonly />
                </b-form-group>
                <b-form-group label="Disabilitato" label-for="ds-dis">
                  <b-form-input id="ds-dis" disabled placeholder="Non modificabile" />
                </b-form-group>
                <b-form-group label="Servizio" label-for="ds-sel">
                  <b-form-select id="ds-sel" v-model="dsServizio" :options="dsServizi" />
                </b-form-group>
                <b-form-group label="Importo" label-for="ds-imp">
                  <b-input-group prepend="€" append=",00">
                    <b-form-input id="ds-imp" placeholder="0" />
                  </b-input-group>
                </b-form-group>
                <b-form-group label="Campo con azione" label-for="ds-cerca">
                  <b-input-group>
                    <b-form-input id="ds-cerca" placeholder="Cerca un paziente" />
                    <b-input-group-append>
                      <b-button variant="secondary">Cerca</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
                <b-form-group label="Data" label-for="ds-data">
                  <b-form-datepicker id="ds-data" v-model="dsData" locale="it" placeholder="Scegli una data" />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Annotazione" label-for="ds-note">
                  <b-form-textarea id="ds-note" rows="3" placeholder="Note per l'operatore" />
                </b-form-group>
                <b-form-group label="Consensi">
                  <b-form-checkbox v-model="dsConsensi.privacy">Ho letto l'informativa</b-form-checkbox>
                  <b-form-checkbox v-model="dsConsensi.promemoria">Voglio ricevere i promemoria</b-form-checkbox>
                  <b-form-checkbox disabled>Non disponibile</b-form-checkbox>
                </b-form-group>
                <b-form-group label="Modalità">
                  <b-form-radio-group v-model="dsModalita" :options="['In presenza', 'Teleconsulto']" />
                </b-form-group>
                <b-form-group label="Notifiche">
                  <b-form-checkbox v-model="dsSwitch" switch>Avvisami via e-mail</b-form-checkbox>
                </b-form-group>
                <b-form-group label="Allegato">
                  <b-form-file placeholder="Nessun file scelto" browse-text="Sfoglia" />
                </b-form-group>
                <b-form-group label="Tag" label-for="ds-tag">
                  <b-form-tags input-id="ds-tag" v-model="dsTags" placeholder="Aggiungi e premi Invio" add-button-text="Aggiungi" />
                </b-form-group>
                <b-form-group label="Campo con errore" label-for="ds-err" invalid-feedback="Inserisci un indirizzo e-mail valido." :state="false">
                  <b-form-input id="ds-err" :state="false" value="maria.rossi@" />
                </b-form-group>
                <b-form-group label="Campo valido" label-for="ds-ok">
                  <b-form-input id="ds-ok" :state="true" value="maria.rossi@esempio.it" />
                </b-form-group>
              </b-col>
            </b-row>
            <h3 class="mt-3">Taglie</h3>
            <div class="ds-row align-items-center">
              <b-form-input size="sm" placeholder="Piccolo · 34" class="ds-w-auto" aria-label="Campo piccolo" />
              <b-form-input placeholder="Normale · 44" class="ds-w-auto" aria-label="Campo normale" />
              <b-form-input size="lg" placeholder="Grande · 50" class="ds-w-auto" aria-label="Campo grande" />
              <b-form-select size="sm" :options="['Select piccola']" class="ds-w-auto" aria-label="Select piccola" />
            </div>
          </div>
        </section>

        <!-- CONTENITORI -->
        <section id="contenitori">
          <h2>Superfici</h2>
          <p class="ds-note">
            Una sola superficie per <code>.shadowedCard</code>, <code>.shadowedCardDeep</code>
            e <code>b-card</code>: bianco all'80% che lascia passare l'aura della pagina,
            nessun bordo, un filo chiaro in alto che dà lo spessore del vetro, ombra
            morbida. Si solleva in hover solo dove è un'azione.
          </p>
          <div class="ds-cards">
            <div class="shadowedCard p-4">
              <p class="ds-eyebrow-dark mb-2">Pressione arteriosa</p>
              <p class="ds-stat mb-1">128<span class="ds-stat__unit">/82</span></p>
              <p class="mb-0 text-muted"><small>Ultima rilevazione, oggi alle 8:15</small></p>
            </div>
            <div class="shadowedCard p-4 point" role="button" tabindex="0">
              <p class="ds-eyebrow-dark mb-2">Prossimo appuntamento</p>
              <p class="ds-t3 mb-1">Teleconsulto cardiologico</p>
              <p class="mb-0 text-muted"><small>Gio 12 set · 10:30 — si solleva in hover</small></p>
            </div>
            <b-card header="Piano terapeutico" footer="Aggiornato il 4 set 2026" class="mb-0">
              <b-card-text>Ramipril 5 mg, una compressa al mattino. Controllo fra 30 giorni.</b-card-text>
              <b-badge variant="success">In corso</b-badge>
            </b-card>
          </div>
        </section>

        <!-- SEGNALI -->
        <section id="segnali">
          <h2>Badge, tag e messaggi</h2>
          <p class="ds-note">
            I badge sono pillole <strong>tinte</strong>: fondo al 12% del colore, testo nel
            colore, scurito quanto basta per leggersi (≥ 4,5:1). Un tag è un badge con la ×.
          </p>
          <div class="shadowedCard p-4">
            <h3>Badge</h3>
            <div class="ds-row align-items-center mb-4">
              <b-badge v-for="v in signalVariants" :key="v" :variant="v">{{ v }}</b-badge>
              <b-badge variant="light">light</b-badge>
              <b-badge variant="severity-high">severità alta</b-badge>
            </div>
            <h3>Tag</h3>
            <div class="ds-row align-items-center mb-4">
              <span v-for="t in dsTagsStatic" :key="t" class="badge b-form-tag d-inline-flex align-items-baseline mw-100 badge-secondary">
                <span class="b-form-tag-content flex-grow-1 text-truncate">{{ t }}</span>
                <button type="button" class="close b-form-tag-remove" :aria-label="'Rimuovi ' + t">×</button>
              </span>
            </div>
            <h3>Messaggi</h3>
            <p class="ds-hint">
              Tinta al 10%, icona disegnata in CSS (nessun markup nei template), testo nel
              colore. <code>info</code> è un avviso neutro, <code>light</code> una nota senza icona.
            </p>
            <b-alert variant="success" show class="mb-2">Prenotazione confermata per giovedì 12 settembre alle 10:30.</b-alert>
            <b-alert variant="warning" show class="mb-2">La tessera sanitaria scade tra 30 giorni: <a href="#segnali" class="alert-link" @click.prevent>aggiornala</a>.</b-alert>
            <b-alert variant="danger" show dismissible class="mb-2">Non è stato possibile inviare il referto. Riprova tra qualche minuto.</b-alert>
            <b-alert variant="info" show class="mb-2">Il teleconsulto si apre 5 minuti prima dell'orario prenotato.</b-alert>
            <b-alert variant="secondary" show class="mb-2">Hai 2 questionari da compilare prima della visita.</b-alert>
            <b-alert variant="primary" show class="mb-2">Nuovo: il piano terapeutico è consultabile anche offline.</b-alert>
            <b-alert variant="light" show class="mb-0">Nota senza icona, per informazioni di contorno.</b-alert>
          </div>
        </section>

        <!-- SEVERITÀ -->
        <section id="severita">
          <h2>Severità clinica</h2>
          <p class="ds-note">
            Scala usata sulle soglie dei parametri monitorati. Va sempre accompagnata da
            un'etichetta: il colore da solo non è un'informazione accessibile.
          </p>
          <div class="shadowedCard p-4">
            <div class="ds-sev">
              <div v-for="s in severity" :key="s.token" class="ds-sev__item" :class="'bg-' + s.token">
                <strong>{{ s.label }}</strong>
                <small class="d-block">{{ s.token }}</small>
              </div>
            </div>
          </div>
        </section>

        <!-- NAVIGAZIONE -->
        <section id="navigazione">
          <h2>Navigazione e selettori</h2>
          <div class="shadowedCard p-4">
            <p class="ds-hint">
              Tab sottolineate: la scheda corrente ha una riga di 2px nel colore dell'azione.
              Dentro a una <code>b-card</code> (<code>b-tabs card</code>) la riga scende sul
              filo della testa. Le pillole per i filtri; il controllo segmentato
              (<code>.themed-button-group</code>) per scegliere tra due o tre viste.
            </p>
            <h3>Tab</h3>
            <b-tabs class="mb-4">
              <b-tab title="Anagrafica" active><p class="pt-3 mb-0">Contenuto della prima scheda.</p></b-tab>
              <b-tab title="Monitoraggio"><p class="pt-3 mb-0">Contenuto della seconda scheda.</p></b-tab>
              <b-tab title="Documenti" disabled><p class="pt-3 mb-0">Non disponibile.</p></b-tab>
            </b-tabs>
            <h3>Tab in una card</h3>
            <b-card no-body class="mb-4">
              <b-tabs card>
                <b-tab title="Soggettivo" active><b-card-text>Il paziente riferisce cefalea da tre giorni.</b-card-text></b-tab>
                <b-tab title="Oggettivo"><b-card-text>PA 128/82, FC 72, SpO₂ 98%.</b-card-text></b-tab>
                <b-tab title="Piano"><b-card-text>Controllo fra 7 giorni.</b-card-text></b-tab>
              </b-tabs>
            </b-card>
            <h3>Pillole</h3>
            <b-tabs pills class="mb-4">
              <b-tab title="Tutte" active><p class="pt-3 mb-0">Tutte le prenotazioni.</p></b-tab>
              <b-tab title="Confermate"><p class="pt-3 mb-0">Solo le confermate.</p></b-tab>
              <b-tab title="In attesa"><p class="pt-3 mb-0">Solo quelle in attesa.</p></b-tab>
            </b-tabs>
            <h3>Controllo segmentato</h3>
            <div class="themed-button-group">
              <b-button-group>
                <b-button variant="outline-secondary" :pressed="dsPeriodo === 'settimana'" @click="dsPeriodo = 'settimana'">Settimana</b-button>
                <b-button variant="outline-secondary" :pressed="dsPeriodo === 'mese'" @click="dsPeriodo = 'mese'">Mese</b-button>
                <b-button variant="outline-secondary" :pressed="dsPeriodo === 'anno'" @click="dsPeriodo = 'anno'">Anno</b-button>
              </b-button-group>
            </div>
            <h3 class="mt-4">Navigazione laterale</h3>
            <p class="ds-note">
              Stesse classi dell'app (<code>app-nav</code>, <code>nav__item</code>,
              <code>nav-active</code>): il CSS è quello vero di <code>_chrome.scss</code>,
              non una copia. Nell'app la colonna è fissa nello scroll e alta quanto la finestra.
            </p>
            <div class="ds-sidebar-demo">
              <div class="app-nav" aria-hidden="true">
                <span>
                  <div class="row">
                    <div class="col">
                      <a href="#navigazione" @click.prevent>
                        <img class="app-nav__logo" :src="require(`@/assets/${scope}/logo.svg`)" alt="" />
                      </a>
                    </div>
                  </div>
                  <div class="row row-cols-1">
                    <div class="col">
                      <p class="nav__item">
                        <a class="router-link-exact-active" href="#navigazione" @click.prevent>
                          <b-icon icon="house-door" font-scale="1.3"></b-icon><span class="ml-2">Dashboard</span>
                        </a>
                      </p>
                      <p class="nav__item">
                        <a href="#navigazione" @click.prevent>
                          <b-icon icon="calendar3" font-scale="1.3"></b-icon><span class="ml-2">Calendario</span>
                        </a>
                      </p>
                      <p class="nav__item">
                        <a href="#navigazione" @click.prevent>
                          <b-icon icon="bar-chart" font-scale="1.3"></b-icon><span class="ml-2">Statistiche</span>
                        </a>
                      </p>
                    </div>
                    <div class="col">
                      <p class="text-font-light-grey"><span class="text-white">Servizi</span></p>
                      <p class="nav__item bg-mdm-light-grey">
                        <span role="button" tabindex="0">
                          <b-icon icon="people" font-scale="1.3"></b-icon><span class="ml-2">Pazienti</span>
                          <b-icon icon="chevron-up" font-scale="1" class="mr-1 ml-auto"></b-icon>
                        </span>
                        <b-collapse visible>
                          <p><a class="nav-active" href="#navigazione" @click.prevent><span class="ml-2">In carico</span></a></p>
                          <p><a href="#navigazione" @click.prevent><span class="ml-2">Da valutare</span></a></p>
                        </b-collapse>
                      </p>
                      <p class="nav__item">
                        <a href="#navigazione" @click.prevent>
                          <b-icon icon="camera-video" font-scale="1.3"></b-icon><span class="ml-2">Teleconsulto</span>
                        </a>
                      </p>
                    </div>
                    <div class="col">
                      <p class="nav__item app-nav__user">
                        <a href="#navigazione" @click.prevent>
                          <b-avatar size="sm" variant="white" rounded>MR</b-avatar><span class="ml-2">Maria Rossi</span>
                        </a>
                      </p>
                      <p class="nav__item">
                        <a href="#navigazione" @click.prevent>
                          <b-icon icon="door-open" font-scale="1.3"></b-icon><span class="ml-2">Esci</span>
                        </a>
                      </p>
                    </div>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </section>

        <!-- SOVRAPPOSTI -->
        <section id="sovrapposti">
          <h2>Elementi sovrapposti</h2>
          <p class="ds-note">
            Tutto ciò che galleggia è vetro. Il menu di <code>b-dropdown</code> è un foglio
            con voci arrotondate e un chevron al posto del triangolino; il tooltip è scuro;
            il popover è una piccola card. La modale è un foglio a 24px con la pagina
            sfocata dietro al velo: sotto i 576px sale dal basso. La × è un bottone tondo,
            a filo del margine, disegnata in CSS. Il toast ha una barra colorata a sinistra.
          </p>
          <div class="shadowedCard p-4">
            <div class="ds-row align-items-center">
              <b-dropdown text="Azioni" variant="white">
                <b-dropdown-header>Prenotazione</b-dropdown-header>
                <b-dropdown-item>Modifica</b-dropdown-item>
                <b-dropdown-item>Sposta</b-dropdown-item>
                <b-dropdown-item active>Conferma</b-dropdown-item>
                <b-dropdown-divider />
                <b-dropdown-item disabled>Stampa</b-dropdown-item>
                <b-dropdown-item variant="danger">Annulla</b-dropdown-item>
              </b-dropdown>
              <b-button v-b-tooltip.hover title="Testo del suggerimento" variant="secondary">Tooltip</b-button>
              <b-button v-b-popover.hover.top="'Contenuto esteso del popover.'"
                        title="Titolo" variant="outline-secondary">
                Popover
              </b-button>
              <b-button variant="secondary" @click="showModal = true">Modale</b-button>
              <b-button variant="mdm-light-grey" @click="showToast">Toast</b-button>
            </div>
            <b-modal v-model="showModal" title="Conferma la prenotazione" ok-title="Conferma" cancel-title="Annulla"
                     ok-variant="secondary" cancel-variant="popup-button" centered>
              <p>Teleconsulto cardiologico, giovedì 12 settembre alle 10:30 con la dott.ssa Bianchi.</p>
              <b-form-group label="Nota per il medico" label-for="ds-modal-note" class="mb-0">
                <b-form-textarea id="ds-modal-note" rows="2" placeholder="Facoltativa" />
              </b-form-group>
            </b-modal>
          </div>
        </section>

        <!-- DATI -->
        <section id="dati">
          <h2>Tabelle e attese</h2>
          <p class="ds-note">
            Niente griglia: intestazione in maiuscoletto grigio, righe separate da un filo
            <code>$n-100</code>, riga in hover tinta di teal. La paginazione è a pillole.
          </p>
          <div class="shadowedCard p-4">
            <b-table :items="tableItems" :fields="tableFields" hover responsive class="mb-3">
              <template #cell(stato)="row">
                <b-badge :variant="row.item.stato === 'Confermata' ? 'success' : row.item.stato === 'In attesa' ? 'warning' : 'secondary'">
                  {{ row.item.stato }}
                </b-badge>
              </template>
            </b-table>
            <b-pagination v-model="dsPage" :total-rows="42" :per-page="6" align="center" class="mb-4"
                          aria-label="Paginazione di esempio" />
            <h3>Avanzamento</h3>
            <div class="ds-stack mb-4">
              <b-progress :value="62" class="mb-2" />
              <b-progress :value="35" variant="success" class="mb-2" />
              <b-progress :value="80" height="1.5rem" show-progress />
            </div>
            <h3>Avatar e scheletri</h3>
            <div class="ds-row align-items-center mb-4">
              <b-avatar text="MR" />
              <b-avatar text="AB" variant="primary" />
              <b-avatar variant="success" icon="people-fill" />
              <b-avatar variant="light" size="lg" text="LV" />
              <b-skeleton width="140px" height="1.25rem" />
              <b-skeleton type="button" />
              <b-skeleton type="avatar" />
            </div>
            <h3>Indicatori di attesa</h3>
            <div class="ds-row align-items-center">
              <b-spinner small />
              <b-spinner />
              <b-spinner type="grow" />
              <b-icon icon="calendar" font-scale="1.6" />
              <b-icon icon="person" font-scale="1.6" />
              <b-icon icon="file-earmark-text" font-scale="1.6" />
            </div>
          </div>
        </section>

        <!-- ICONE -->
        <section id="icone">
          <h2>Icone dei servizi</h2>
          <p class="ds-note">
            Set proprio di ConCura, monocromatico: griglia 24×24, tratto 1.75, angoli
            tondi, colore dal testo. I file sono in
            <code>src/assets/concura/icons/servizi/</code> (uno per icona più lo sprite
            <code>servizi.svg</code>) e valgono anche per il sito pubblico. Le icone
            generiche dell'interfaccia restano le Bootstrap Icons.
          </p>
          <div class="shadowedCard p-4">
            <div class="ds-icons">
              <div v-for="n in serviceIcons" :key="n" class="ds-icons__item">
                <span class="cc-icon" :class="'cc-icon--' + n" aria-hidden="true"></span>
                <span class="ds-icons__name">{{ n }}</span>
              </div>
            </div>
            <h3 class="mt-4">Dentro alle tessere</h3>
            <div class="action-grid" role="list">
              <div class="action-tile" role="listitem">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--visita" aria-hidden="true"></span></span>
                <span class="action-tile__label">Visite e prestazioni</span>
              </div>
              <div class="action-tile" role="listitem">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--monitoraggio" aria-hidden="true"></span></span>
                <span class="action-tile__label">Monitoraggio</span>
              </div>
              <div class="action-tile action-tile--urgent" role="listitem">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--teleconsulto-rapido" aria-hidden="true"></span></span>
                <span class="action-tile__label">Teleconsulto rapido</span>
              </div>
              <div class="action-tile action-tile--pediatric" role="listitem">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--pediatrico" aria-hidden="true"></span></span>
                <span class="action-tile__label">Pediatrico</span>
              </div>
            </div>
          </div>
        </section>

        <!-- PAGINE -->
        <section id="pagine">
          <h2>Pattern di pagina</h2>
          <p class="ds-note">
            Il vocabolario con cui si compongono le pagine dei profili
            (<code>_pages.scss</code>): testa di sezione, tessere azione, tessere
            numero, righe di lista. Le pagine usano queste classi e non definiscono
            colori, raggi o ombre propri. Prima applicazione: la home del cittadino.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <div class="section-head mb-3 point" role="button" tabindex="0">
              <b-icon icon="journal-medical" font-scale="1.2" class="section-head__icon"></b-icon>
              <h2 class="section-title mb-0 flex-grow-1">Testa di sezione con rimando</h2>
              <b-icon icon="chevron-right" class="section-head__chevron"></b-icon>
            </div>
            <h3 class="section-eyebrow mb-3">Tessere azione</h3>
            <div class="action-grid mb-4" role="list">
              <div class="action-tile point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><b-icon icon="calendar2-check-fill" aria-hidden="true"></b-icon></span>
                <span class="action-tile__label">Visite e prestazioni</span>
              </div>
              <div class="action-tile point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><b-icon icon="bag-plus-fill" aria-hidden="true"></b-icon></span>
                <span class="action-tile__label">Farmaci</span>
              </div>
              <div class="action-tile action-tile--urgent point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><b-icon icon="camera-video-fill" aria-hidden="true"></b-icon></span>
                <span class="action-tile__label">Teleconsulto rapido</span>
              </div>
              <div class="action-tile action-tile--pediatric point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><b-icon icon="emoji-smile-fill" aria-hidden="true"></b-icon></span>
                <span class="action-tile__label">Teleconsulto pediatrico</span>
              </div>
              <div class="action-tile action-tile--muted" role="listitem">
                <span class="action-tile__icon"><b-icon icon="lock-fill" aria-hidden="true"></b-icon></span>
                <span class="action-tile__label">Servizio non attivo</span>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Tessere azione con fotografia</h3>
            <p class="ds-note mb-3">
              La variante alta delle tessere azione: la fotografia riempie la
              card, un velo navy la scurisce dal basso, il badge con l'icona sta
              in alto a sinistra e il titolo in basso a sinistra. La fotografia
              si dichiara in <code>_pages.scss</code> sul modificatore
              (<code>.action-card--paziente</code>) e non nel componente: è una
              risorsa di ConCura. Finché non c'è, resta la filigrana dell'icona
              sul navy del marchio. In modalità accessibile l'immagine sparisce
              e resta la superficie piena con il bordo d'accento.
            </p>
            <div class="action-grid action-grid--four mb-4" role="list">
              <div class="action-card point action-card--paziente" role="listitem button" tabindex="0">
                <span class="action-card__badge" aria-hidden="true"><span class="cc-icon cc-icon--paziente"></span></span>
                <span class="action-card__plus" aria-hidden="true"><b-icon icon="plus"></b-icon></span>
                <span class="action-card__label">Nuovo paziente</span>
              </div>
              <div class="action-card point action-card--prestazione" role="listitem button" tabindex="0">
                <span class="action-card__badge" aria-hidden="true"><span class="cc-icon cc-icon--visita"></span></span>
                <span class="action-card__plus" aria-hidden="true"><b-icon icon="plus"></b-icon></span>
                <span class="action-card__label">Nuova prestazione</span>
              </div>
              <div class="action-card point action-card--teleconsulto" role="listitem button" tabindex="0">
                <span class="action-card__badge" aria-hidden="true"><span class="cc-icon cc-icon--teleconsulto"></span></span>
                <span class="action-card__plus" aria-hidden="true"><b-icon icon="plus"></b-icon></span>
                <span class="action-card__label">Nuovo teleconsulto</span>
              </div>
              <div class="action-card point action-card--terapia" role="listitem button" tabindex="0">
                <span class="action-card__badge" aria-hidden="true"><span class="cc-icon cc-icon--terapia"></span></span>
                <span class="action-card__plus" aria-hidden="true"><b-icon icon="plus"></b-icon></span>
                <span class="action-card__label">Nuova terapia</span>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Tessere numero</h3>
            <div class="stat-grid mb-4">
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--success"><b-icon icon="heart-fill"></b-icon></div>
                <div class="stat-tile__value">72<span class="stat-tile__unit">bpm</span></div>
                <div class="stat-tile__label">Frequenza cardiaca</div>
              </div>
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--warning"><b-icon icon="speedometer2"></b-icon></div>
                <div class="stat-tile__value">142<span class="stat-tile__unit">mmHg</span></div>
                <div class="stat-tile__label">Pressione sistolica</div>
              </div>
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--danger"><b-icon icon="wind"></b-icon></div>
                <div class="stat-tile__value">89<span class="stat-tile__unit">%</span></div>
                <div class="stat-tile__label">Saturazione</div>
              </div>
              <div class="stat-tile point" role="button" tabindex="0">
                <div class="stat-tile__value">3</div>
                <div class="stat-tile__label">Appuntamenti</div>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Righe di lista</h3>
            <div class="list-row point" role="button" tabindex="0">
              <div class="list-row__icon list-row__icon--teleconsulto"><b-icon icon="camera-video-fill"></b-icon></div>
              <div class="list-row__body">
                <p class="list-row__title text-truncate mb-0">Teleconsulto cardiologico</p>
                <p class="list-row__meta text-truncate mb-0">Centro Cardiologico Bianchi</p>
              </div>
              <p class="list-row__aside mb-0">12 set, 10:30</p>
              <b-icon icon="chevron-right" class="list-row__chevron" font-scale="0.85"></b-icon>
            </div>
            <div class="list-row point" role="button" tabindex="0">
              <div class="list-row__icon"><b-icon icon="calendar2-check-fill"></b-icon></div>
              <div class="list-row__body">
                <p class="list-row__title text-truncate mb-0">Visita dermatologica</p>
                <p class="list-row__meta text-truncate mb-0">Poliambulatorio Mureddu</p>
              </div>
              <p class="list-row__aside mb-0">18 set, 15:00</p>
              <b-icon icon="chevron-right" class="list-row__chevron" font-scale="0.85"></b-icon>
            </div>
            <p class="list-row__empty">Nessun altro appuntamento in programma.</p>
          </div>
          <p class="ds-note">
            Home dell'operatore: la <strong>testa di pagina</strong> con il saluto,
            gli <strong>accessi rapidi</strong> (chip) e la <strong>barra del giorno</strong>
            (ora, selettore della data, informazioni); le <strong>card servizio</strong>
            con il numero da gestire e le righe di stato; la pila di righe.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <header class="page-head">
              <div class="page-head__main">
                <h1 class="page-head__title">Buongiorno, Paola</h1>
                <div class="page-head__quick" role="group" aria-label="Accessi rapidi">
                  <button type="button" class="action-chip action-chip--urgent">
                    <span class="action-chip__icon" aria-hidden="true"><span class="cc-icon cc-icon--teleconsulto-rapido"></span></span>
                    Teleconsulto rapido
                  </button>
                  <button type="button" class="action-chip action-chip--pediatric">
                    <span class="action-chip__icon" aria-hidden="true"><span class="cc-icon cc-icon--pediatrico"></span></span>
                    Teleconsulto pediatrico
                  </button>
                  <button type="button" class="action-chip">
                    <span class="action-chip__icon" aria-hidden="true"><span class="cc-icon cc-icon--paziente"></span></span>
                    Arruola paziente
                  </button>
                </div>
              </div>
              <div class="page-head__tools">
                <div class="day-bar">
                  <span class="day-bar__hour">10:42</span>
                  <b-form-datepicker id="ds-day" v-model="dsDay" class="day-bar__date" locale="it" start-weekday="1"
                    :date-format-options="{ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }"></b-form-datepicker>
                  <b-button variant="white" class="btn-icon" aria-label="Informazioni"><b-icon icon="info-circle" aria-hidden="true"></b-icon></b-button>
                </div>
                <b-form-checkbox switch class="page-head__toggle" checked>Visualizza dashboard standard</b-form-checkbox>
              </div>
            </header>
            <h3 class="section-eyebrow mb-3">Card servizio</h3>
            <div class="service-grid mb-4">
              <article class="service-card">
                <div class="service-card__head">
                  <span class="service-card__icon" aria-hidden="true"><span class="cc-icon cc-icon--visita"></span></span>
                  <h3 class="service-card__title">Visite e Prestazioni</h3>
                </div>
                <p class="service-card__value">7<span class="service-card__unit">da gestire</span></p>
                <div class="service-card__rows">
                  <div class="service-card__row" role="button" tabindex="0"><span>In attesa</span><span class="service-card__num">4</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Confermate</span><span class="service-card__num">3</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Annullate</span><span class="service-card__num">1</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Evase</span><span class="service-card__num">12</span></div>
                </div>
              </article>
              <article class="service-card">
                <div class="service-card__head">
                  <span class="service-card__icon" aria-hidden="true"><span class="cc-icon cc-icon--teleconsulto"></span></span>
                  <h3 class="service-card__title">Teleconsulti</h3>
                </div>
                <p class="service-card__value">2<span class="service-card__unit">da gestire</span></p>
                <div class="service-card__rows">
                  <div class="service-card__row" role="button" tabindex="0"><span>In attesa</span><span class="service-card__num">2</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Confermati</span><span class="service-card__num">0</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Annullati</span><span class="service-card__num">0</span></div>
                  <div class="service-card__row" role="button" tabindex="0"><span>Evasi</span><span class="service-card__num">5</span></div>
                </div>
              </article>
            </div>
            <h3 class="section-eyebrow mb-3">ASL di riferimento</h3>
            <div class="asl-card">
              <b-avatar variant="white" rounded="sm" size="3.5rem" text="ASL" class="asl-card__logo"></b-avatar>
              <div class="asl-card__body">
                <h5 class="asl-card__title">ASL Roma 1</h5>
                <p class="asl-card__meta">Borgo Santo Spirito 3, Roma</p>
              </div>
            </div>
          </div>
          <p class="ds-note">
            Liste dell'operatore (prenotazioni, teleconsulti, pazienti, calendario):
            <strong>chip di stato</strong>, <strong>barra dei filtri</strong> con la
            <strong>ricerca con chiave</strong>, elenco in card. Scheda paziente:
            <strong>testa del paziente</strong> e <strong>schede di sezione</strong>.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <header class="page-head">
              <div class="page-head__main">
                <h1 class="page-title">Prenotazioni</h1>
              </div>
              <div class="page-head__tools">
                <b-button variant="white" class="btn-icon" aria-label="Informazioni"><b-icon icon="info-circle" aria-hidden="true"></b-icon></b-button>
                <div class="themed-button-group">
                  <b-button-group>
                    <b-button variant="secondary" size="sm" class="active" aria-label="Vista elenco"><b-icon icon="list" aria-hidden="true" class="mx-2"></b-icon></b-button>
                    <b-button variant="outline-secondary" size="sm" aria-label="Vista calendario"><b-icon icon="calendar3-week-fill" aria-hidden="true" class="mx-2"></b-icon></b-button>
                  </b-button-group>
                </div>
              </div>
            </header>
            <div class="filter-chips" role="group" aria-label="Stato">
              <button type="button" class="filter-chip filter-chip--active">Tutte</button>
              <button type="button" class="filter-chip">In attesa</button>
              <button type="button" class="filter-chip">Confermate</button>
              <button type="button" class="filter-chip">Annullate</button>
              <button type="button" class="filter-chip">Evase</button>
            </div>
            <div class="filter-bar shadowedCard">
              <div class="filter-bar__field filter-bar__field--search">
                <div class="search-toggle" role="group">
                  <button type="button" class="search-toggle__tab search-toggle__tab--active" aria-pressed="true">Codice fiscale</button>
                  <button type="button" class="search-toggle__tab" aria-pressed="false">Codice richiesta</button>
                </div>
                <div class="search-field">
                  <b-form-input type="search" placeholder="Codice fiscale..." aria-label="Codice fiscale"></b-form-input>
                  <b-icon icon="search" class="search-field__icon" aria-hidden="true"></b-icon>
                </div>
              </div>
              <div class="filter-bar__field">
                <p class="filter-bar__label">Periodo</p>
                <b-form-select :options="['Questa settimana', 'Questo mese', 'Tutte']" value="Questa settimana"></b-form-select>
              </div>
              <div class="filter-bar__field">
                <p class="filter-bar__label">Servizio</p>
                <b-form-select :options="['Tutti', 'Prestazione', 'Prodotto']" value="Tutti"></b-form-select>
              </div>
              <div class="filter-bar__field filter-bar__field--auto">
                <p class="filter-bar__label">Mostrane</p>
                <b-form-select :options="[10, 25, 50]" :value="10"></b-form-select>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Testa del paziente e schede di sezione</h3>
            <section class="shadowedCard p-4 patient-head">
              <div class="patient-head__top">
                <div class="patient-head__identity">
                  <b-avatar variant="white" rounded="sm" size="3.5rem" text="MR"></b-avatar>
                  <h1 class="patient-head__name" role="link" tabindex="0">Maria Rossi <b-icon icon="file-earmark-person-fill" aria-hidden="true"></b-icon></h1>
                </div>
                <div class="patient-head__tags">
                  <p class="patient-head__tag"><b-icon icon="patch-check-fill" aria-hidden="true"></b-icon> Monitoraggio attivo</p>
                  <p class="patient-head__tag"><b-icon icon="patch-check-fill" aria-hidden="true"></b-icon> Protocollo ERAS</p>
                </div>
                <div class="patient-head__actions">
                  <b-button variant="secondary" size="sm"><b-icon icon="clipboard-check" class="mr-1"></b-icon> Avvia presa in carico</b-button>
                  <b-button variant="outline-secondary" size="sm">Modifica anagrafica</b-button>
                </div>
              </div>
              <div class="patient-head__grid">
                <div><p class="patient-head__label">Genere</p><p class="patient-head__value">femmina</p></div>
                <div><p class="patient-head__label">Data di nascita</p><p class="patient-head__value">12/03/1968</p></div>
                <div><p class="patient-head__label">Codice fiscale</p><p class="patient-head__value">RSSMRA68C52H501X</p></div>
                <div><p class="patient-head__label">Telefono</p><p class="patient-head__value">+39 333 1234567</p></div>
                <div><p class="patient-head__label">Email</p><p class="patient-head__value">maria.rossi@example.it</p></div>
                <div><p class="patient-head__label">Comune</p><p class="patient-head__value">Roma</p></div>
              </div>
            </section>
            <div class="section-tabs" role="group" aria-label="Sezioni della scheda">
              <b-button variant="secondary" size="sm" class="active"><b-icon icon="journal-medical" aria-hidden="true"></b-icon><span>PSS</span></b-button>
              <b-button variant="outline-secondary" size="sm"><b-icon icon="calendar2-check" aria-hidden="true"></b-icon><span>Prenotazioni</span></b-button>
              <b-button variant="outline-secondary" size="sm"><b-icon icon="bar-chart" aria-hidden="true"></b-icon><span>Archivio sanitario</span></b-button>
              <b-button variant="outline-secondary" size="sm"><b-icon icon="bookmark" aria-hidden="true"></b-icon><span>Patologie</span></b-button>
              <b-button variant="outline-secondary" size="sm"><b-icon icon="clipboard" aria-hidden="true"></b-icon><span>Terapie</span></b-button>
              <b-button variant="outline-secondary" size="sm"><b-icon icon="bell" aria-hidden="true"></b-icon><span>Notifiche</span></b-button>
            </div>
          </div>
        </section>

        <!-- PAGINE DEL CITTADINO -->
        <section id="cittadino">
          <h2>Pagine del cittadino</h2>
          <p class="ds-note">
            La regola è <strong>minimo e semplice</strong>: il titolo della pagina
            sta nell'header dell'app e non si ripete; le sezioni sono
            <code>SectionTabs</code>; sopra a una lista c'è una barra con i chip di
            filtro a sinistra e i selettori a destra; gli stati vuoti sono un'icona
            tenue e una riga. Nella home, "Contatta un medico" è la prima card.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h2 class="section-title">Contatta un medico</h2>
            <div class="contact-grid mb-4" role="list">
              <div class="action-tile action-tile--urgent point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--teleconsulto-rapido" aria-hidden="true"></span></span>
                <span class="action-tile__label">Teleconsulto Rapido</span>
                <span class="action-tile__hint">Un medico ti contatta entro 60 minuti</span>
              </div>
              <div class="action-tile point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--chat" aria-hidden="true"></span></span>
                <span class="action-tile__label">Chat con il medico</span>
                <span class="action-tile__hint">Scrivi al medico del tuo teleconsulto</span>
              </div>
              <div class="action-tile action-tile--pediatric point" role="listitem button" tabindex="0">
                <span class="action-tile__icon"><span class="cc-icon cc-icon--pediatrico" aria-hidden="true"></span></span>
                <span class="action-tile__label">Teleconsulto Pediatrico</span>
                <span class="action-tile__hint">Per i tuoi figli, risponde un pediatra</span>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Numeri compatti (profilo sanitario in home)</h3>
            <div class="stat-grid stat-grid--compact mb-4">
              <div class="stat-tile point">
                <div class="stat-tile__icon stat-tile__icon--success"><b-icon icon="heart-fill"></b-icon></div>
                <div class="stat-tile__value">72<span class="stat-tile__unit">bpm</span></div>
                <div class="stat-tile__label">Frequenza cardiaca</div>
              </div>
              <div class="stat-tile point">
                <div class="stat-tile__icon stat-tile__icon--warning"><b-icon icon="speedometer2"></b-icon></div>
                <div class="stat-tile__value">142<span class="stat-tile__unit">mmHg</span></div>
                <div class="stat-tile__label">Pressione sistolica</div>
              </div>
              <div class="stat-tile point">
                <div class="stat-tile__icon"><b-icon icon="clipboard-data"></b-icon></div>
                <div class="stat-tile__value">74<span class="stat-tile__unit">kg</span></div>
                <div class="stat-tile__label">Peso</div>
              </div>
              <div class="stat-tile point">
                <div class="stat-tile__icon stat-tile__icon--danger"><b-icon icon="thermometer"></b-icon></div>
                <div class="stat-tile__value">38.4<span class="stat-tile__unit">°C</span></div>
                <div class="stat-tile__label">Temperatura</div>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Convenzioni compatte</h3>
            <div class="convenzioni--compact mb-4">
              <div class="convenzioni-grid">
                <div class="card convenzione">
                  <div class="convenzione__hero"><span class="cc-icon cc-icon--supporto" aria-hidden="true"></span></div>
                  <div class="convenzione__body">
                    <h4 class="convenzione__title">ConCura &amp; Partner</h4>
                    <div class="convenzione__actions">
                      <button type="button" class="btn btn-secondary btn-block">Richiedi il voucher</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <h3 class="section-eyebrow mb-3">Barra di una lista, conteggio, stato vuoto</h3>
            <div class="list-toolbar">
              <div class="filter-chips" role="group">
                <button type="button" class="filter-chip filter-chip--active" aria-pressed="true">Le mie prenotazioni</button>
                <button type="button" class="filter-chip" aria-pressed="false">Assistiti</button>
              </div>
              <div class="list-toolbar__end">
                <label for="ds-anno" class="section-eyebrow mb-0">Anno</label>
                <select id="ds-anno" class="custom-select custom-select-sm w-auto"><option>2026</option><option>2025</option></select>
              </div>
            </div>
            <div class="empty-state">
              <b-icon icon="inbox" class="empty-state__icon" aria-hidden="true"></b-icon>
              <p>Nessuna prenotazione.</p>
            </div>
            <div class="list-pager">
              <p class="list-pager__count">1-10 di 32</p>
            </div>
            <h3 class="section-eyebrow mt-4 mb-3">Link "indietro" e cifre</h3>
            <button type="button" class="back-link"><b-icon icon="arrow-left" aria-hidden="true"></b-icon> Il mio circolo di cura</button>
            <p class="mb-0"><span class="figure">60,00 €</span> <span class="figure figure--sm">/anno</span></p>
          </div>
        </section>

        <!-- ARCHIVIO, CALENDARIO, PASSI, PROGETTI -->
        <section id="archivio">
          <h2>Rilevazioni, calendario, passi</h2>
          <p class="ds-note">
            Le rilevazioni dell'archivio sanitario sono schede compatte con una
            barra di severità sul bordo alto (prima erano riquadri tondi con un
            bordo spesso su tutto il perimetro). Nelle modali di inserimento la
            ricerca è un campo normale con la lente e le voci sono schede
            rettangolari, non pillole alte.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h3 class="section-eyebrow mb-3">Schede di rilevazione</h3>
            <div class="measure-grid mb-4">
              <div class="measure-card measure-card--success" role="button" tabindex="0">
                <p class="measure-card__name">Frequenza cardiaca</p>
                <p class="measure-card__value">72<span class="measure-card__unit">bpm</span></p>
                <p class="measure-card__meta">20 agosto 2026 18:17</p>
              </div>
              <div class="measure-card measure-card--warning" role="button" tabindex="0">
                <p class="measure-card__name">
                  Pressione sistolica
                  <span class="measure-card__flag"><b-icon icon="eye-fill" aria-hidden="true"></b-icon></span>
                </p>
                <p class="measure-card__value">142<span class="measure-card__unit">mmHg</span></p>
                <p class="measure-card__meta">15 aprile 2026 17:22</p>
              </div>
              <div class="measure-card measure-card--danger" role="button" tabindex="0">
                <p class="measure-card__name">Temperatura</p>
                <p class="measure-card__value">38.6<span class="measure-card__unit">°C</span></p>
                <p class="measure-card__meta">5 marzo 2026 18:00</p>
              </div>
              <div class="measure-card" role="button" tabindex="0">
                <p class="measure-card__name">Peso</p>
                <p class="measure-card__value">74<span class="measure-card__unit">kg</span></p>
                <p class="measure-card__meta">16 gennaio 2026 18:32</p>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Ricerca e scelta in una modale</h3>
            <div class="modal-search">
              <label for="ds-cerca-esame" class="section-eyebrow mb-2">Trova parametro</label>
              <div class="search-field">
                <b-form-input id="ds-cerca-esame" type="search" placeholder="Cerca…" />
                <b-icon icon="search" class="search-field__icon" aria-hidden="true"></b-icon>
              </div>
            </div>
            <b-row class="mb-4">
              <b-col class="col-12 col-md-4 col-lgx-3 px-1">
                <b-button block variant="secondary" class="option-btn mb-2">Frequenza cardiaca</b-button>
              </b-col>
              <b-col class="col-12 col-md-4 col-lgx-3 px-1">
                <b-button block variant="outline-secondary" class="option-btn mb-2">Pressione sistolica (massima)</b-button>
              </b-col>
              <b-col class="col-12 col-md-4 col-lgx-3 px-1">
                <b-button block variant="outline-secondary" class="option-btn mb-2">Saturazione</b-button>
              </b-col>
            </b-row>

            <h3 class="section-eyebrow mb-3">Calendario</h3>
            <div class="cal-nav">
              <b-button variant="white" class="btn-icon btn-sm" aria-label="Settimana precedente">
                <b-icon icon="chevron-left" aria-hidden="true"></b-icon>
              </b-button>
              <h2 class="cal-nav__period">12 gen - 18 gen</h2>
              <b-button variant="white" class="btn-icon btn-sm" aria-label="Settimana successiva">
                <b-icon icon="chevron-right" aria-hidden="true"></b-icon>
              </b-button>
            </div>
            <p class="cal-head mb-2">
              <span class="cal-head__day">Mar</span>
              <span class="cal-head__date">13</span>
            </p>
            <div class="d-flex flex-wrap mb-3" style="gap: 0.5rem">
              <button type="button" class="cal-event bg-confermato" style="width: 9rem">
                <b-icon icon="file-ppt" class="cal-event__icon" aria-hidden="true"></b-icon>
                <span>17:15</span>
              </button>
              <button type="button" class="cal-event bg-evaso" style="width: 9rem">
                <b-icon icon="camera-video" class="cal-event__icon" aria-hidden="true"></b-icon>
                <span>09:00</span>
              </button>
              <button type="button" class="cal-event cal-event--personale" style="width: 9rem">
                <b-icon icon="tag" rotate="135" class="cal-event__icon" aria-hidden="true"></b-icon>
                <span>11:30</span>
              </button>
            </div>
            <div class="cal-legend mb-4">
              <div class="cal-legend__group">
                <span class="cal-legend__item confermato"><span class="cal-legend__dot"></span><span class="text-font-grey">Confermato</span></span>
                <span class="cal-legend__item evaso"><span class="cal-legend__dot"></span><span class="text-font-grey">Evaso</span></span>
                <span class="cal-legend__item inoltrato-in-attesa"><span class="cal-legend__dot"></span><span class="text-font-grey">In attesa</span></span>
                <span class="cal-legend__item rifiutato-annullato"><span class="cal-legend__dot"></span><span class="text-font-grey">Annullato</span></span>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Barra dei passi</h3>
            <div class="step-bar">
              <span class="step-bar__item step-bar__item--done">Di cosa hai bisogno</span>
              <span class="step-bar__item step-bar__item--done">Per chi</span>
              <span class="step-bar__item step-bar__item--active">Presso</span>
              <span class="step-bar__item">Data</span>
              <span class="step-bar__item">Conferma</span>
              <span class="step-bar__item">Riepilogo</span>
            </div>

            <h3 class="section-eyebrow mb-3">Progetto con colore proprio</h3>
            <div class="project" style="--project-accent: #FF6B35">
              <div class="d-flex align-items-center mb-3">
                <h4 class="project__section-title mb-0">Articoli</h4>
                <b-button size="sm" class="ml-3 project__btn">Vedi tutti</b-button>
                <b-button size="sm" class="ml-2 project__btn-ghost">Modifica</b-button>
              </div>
              <div class="post-row">
                <div class="post-row__body">
                  <p class="post-row__date">05/03/26</p>
                  <p class="post-row__title">Come prepararsi alla visita</p>
                </div>
                <b-icon icon="chevron-right" class="project__chevron" font-scale="1.5"></b-icon>
              </div>
            </div>
          </div>
        </section>

        <!-- SUGGERIMENTI, CHAT, AGENDA -->
        <section id="chat">
          <h2>Suggerimenti, chat, agenda</h2>
          <p class="ds-note">
            Pattern arrivati con le funzionalità di <code>clinical_admin</code> e
            <code>medChat</code>: il menu dei suggerimenti sotto a un campo, la chat
            clinica, l'appuntamento nella cella dell'agenda, il riquadro tenue.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h3 class="section-eyebrow mb-3">Suggerimenti mentre si scrive</h3>
            <div class="filter-bar filter-bar--plain mb-4">
              <div class="filter-bar__field position-relative">
                <label class="filter-bar__label" for="ds-sugg">Cognome</label>
                <b-form-input id="ds-sugg" value="Ro" />
                <div class="suggest-list" style="position: static; margin-top: .5rem">
                  <button type="button" class="suggest-list__item">
                    <span class="suggest-list__title">Rossi Maria</span>
                    <span class="suggest-list__meta">CF: RSSMRA68C52H501X</span>
                  </button>
                  <button type="button" class="suggest-list__item">
                    <span class="suggest-list__title">Romano Luca</span>
                    <span class="suggest-list__meta">CF: RMNLCU80A01H501U</span>
                  </button>
                </div>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Chat clinica</h3>
            <div class="chat-layout mb-4">
              <aside class="chat-channels">
                <p class="section-eyebrow mb-2">Canali (2)</p>
                <div class="chat-channels__list">
                  <button type="button" class="list-row chat-channel chat-channel--active">
                    <span class="list-row__icon" aria-hidden="true"><b-icon icon="person-fill"></b-icon></span>
                    <span class="list-row__body">
                      <span class="list-row__title d-block">Dott.ssa Bianchi</span>
                      <span class="list-row__meta d-block">Cardiologia</span>
                    </span>
                    <b-badge variant="danger" pill>2</b-badge>
                  </button>
                  <button type="button" class="list-row chat-channel">
                    <span class="list-row__icon" aria-hidden="true"><b-icon icon="person-fill"></b-icon></span>
                    <span class="list-row__body">
                      <span class="list-row__title d-block">Inf. Verdi</span>
                      <span class="list-row__meta d-block">Monitoraggio</span>
                    </span>
                  </button>
                </div>
              </aside>
              <section>
                <div class="chat-thread" style="min-height: 12rem">
                  <div class="chat-bubble">
                    <p class="chat-bubble__meta"><span>Dott.ssa Bianchi</span><span>09:12</span></p>
                    <p class="chat-bubble__text">Buongiorno, come va la pressione stamattina?</p>
                  </div>
                  <div class="chat-bubble chat-bubble--mine">
                    <p class="chat-bubble__meta"><span>Tu</span><span>09:20</span></p>
                    <p class="chat-bubble__text">125/80, tutto regolare. Allego la misurazione.</p>
                    <button type="button" class="chat-bubble__file"><b-icon icon="paperclip" class="mr-1"></b-icon> pressione.pdf (pdf)</button>
                  </div>
                </div>
                <div class="chat-compose">
                  <b-button variant="white" class="btn-icon" aria-label="Allega"><b-icon icon="paperclip"></b-icon></b-button>
                  <b-form-input placeholder="Scrivi un messaggio al medico…" />
                  <b-button variant="primary"><b-icon icon="cursor-fill" class="mr-1"></b-icon>Invia</b-button>
                </div>
              </section>
            </div>

            <h3 class="section-eyebrow mb-3">Tile del calendario e riquadro tenue</h3>
            <b-row>
              <b-col cols="12" md="4">
                <div class="cal-group">
                  <div class="cal-group__head">
                    <b-icon icon="calendar-event" class="flex-shrink-0" aria-hidden="true"></b-icon>
                    <span class="cal-group__ora">10:00</span>
                    <span class="cal-group__nome">Maria Rossi</span>
                    <b-icon icon="chevron-down" class="cal-group__chevron flex-shrink-0"></b-icon>
                  </div>
                </div>
                <div class="cal-group">
                  <div class="cal-group__head">
                    <b-icon icon="journal-medical" class="flex-shrink-0"></b-icon>
                    <b-icon icon="circle-fill" class="flex-shrink-0 text-primary"></b-icon>
                    <span class="cal-group__ora">11:00</span>
                    <span class="cal-group__nome">Luigi Bianchi</span>
                    <b-icon icon="chevron-down" class="cal-group__chevron flex-shrink-0"></b-icon>
                  </div>
                </div>
                <div class="cal-group">
                  <div class="p-2 text-left">
                    <div class="d-flex align-items-center mb-1">
                      <b-icon icon="calendar-event" class="mr-1" aria-hidden="true"></b-icon>
                      <span class="list-row__title">Maria Rossi</span>
                      <b-badge variant="light" pill class="ml-auto">30 min</b-badge>
                    </div>
                    <div class="cal-note__meta text-center mb-2">10:00 - 10:30</div>
                    <div class="tint-box tint-box--sm text-center">
                      <p class="list-row__title mb-1">Maria Rossi</p>
                      <p class="list-row__meta mb-0">Visita cardiologica</p>
                    </div>
                  </div>
                </div>
              </b-col>
              <b-col cols="12" md="8">
                <div class="tint-box text-left">
                  <h3 class="section-eyebrow mb-3">Dati del paziente</h3>
                  <div class="patient-head__grid">
                    <div><p class="patient-head__label">Nome e cognome</p><p class="patient-head__value">Maria Rossi</p></div>
                    <div><p class="patient-head__label">Codice fiscale</p><p class="patient-head__value">RSSMRA68C52H501X</p></div>
                    <div><p class="patient-head__label">Telefono</p><p class="patient-head__value">+39 333 1234567</p></div>
                  </div>
                </div>
              </b-col>
            </b-row>
          </div>
        </section>

        <!-- AVVISO GLOBALE, IMPOSTAZIONI, PIANI -->
        <section id="impostazioni">
          <h2>Avviso globale, impostazioni, piani</h2>
          <p class="ds-note">
            L'avviso globale (store <code>alert</code>) è un foglio flottante in alto a
            destra; qui è mostrato fermo. Le impostazioni sono una griglia di card
            uguali; i consensi sono righe con lo stato. I piani nel nastro sono tessere.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h3 class="section-eyebrow mb-3">Avviso globale</h3>
            <div class="d-flex flex-wrap mb-4" style="gap: 1rem">
              <div class="alert alert-success alert-dismissible app-toast" style="position: static; width: 22rem; animation: none">
                Modifiche salvate correttamente.
                <button type="button" class="close" aria-label="Chiudi">×</button>
              </div>
              <div class="alert alert-danger alert-dismissible app-toast" style="position: static; width: 22rem; animation: none">
                Non è stato possibile completare l'operazione.
                <button type="button" class="close" aria-label="Chiudi">×</button>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Card delle impostazioni</h3>
            <div class="settings-pair mb-4">
              <section class="shadowedCard p-4 settings-card">
                <div class="section-head mb-3">
                  <b-icon icon="key" class="section-head__icon" aria-hidden="true"></b-icon>
                  <h2 class="section-title mb-0">Dati di accesso</h2>
                </div>
                <div class="patient-head__grid">
                  <div><p class="patient-head__label">Username</p><p class="patient-head__value">maria.rossi@esempio.it</p></div>
                  <div><p class="patient-head__label">Password</p><p class="patient-head__value">••••••••</p></div>
                </div>
                <div class="settings-card__actions">
                  <b-button variant="secondary" size="sm">Cambia password</b-button>
                </div>
              </section>
              <section class="shadowedCard p-4 settings-card">
                <div class="section-head mb-3">
                  <b-icon icon="shield-check" class="section-head__icon" aria-hidden="true"></b-icon>
                  <h2 class="section-title mb-0">Termini e privacy</h2>
                </div>
                <div class="list-stack">
                  <div class="list-row">
                    <span class="list-row__icon list-row__icon--ok" aria-hidden="true"><b-icon icon="check-lg"></b-icon></span>
                    <div class="list-row__body"><p class="list-row__title mb-0">Hai preso visione dei <a class="text-underlined" href="#">Termini d'uso</a></p></div>
                    <b-button size="sm" variant="outline-secondary">Modifica</b-button>
                  </div>
                  <div class="list-row">
                    <span class="list-row__icon list-row__icon--no" aria-hidden="true"><b-icon icon="x-lg"></b-icon></span>
                    <div class="list-row__body"><p class="list-row__title mb-0">Non acconsenti alle comunicazioni commerciali</p></div>
                    <b-button size="sm" variant="outline-secondary">Modifica</b-button>
                  </div>
                </div>
              </section>
            </div>

            <h3 class="section-eyebrow mb-3">Tessere dei piani</h3>
            <div class="d-flex flex-wrap" style="gap: 1rem">
              <div class="plan-tile">
                <p class="plan-tile__name">ConCura EASY</p>
                <p class="plan-tile__price"><span class="figure">36€</span><span class="plan-tile__period">/ anno</span></p>
                <button type="button" class="back-link plan-tile__more">Leggi di più <b-icon icon="chevron-right"></b-icon></button>
                <div class="plan-tile__actions"><b-button size="sm" variant="secondary" block>Acquista</b-button></div>
              </div>
              <div class="plan-tile">
                <p class="plan-tile__name">ConCura FAMILY</p>
                <p class="plan-tile__price"><span class="figure">120€</span><span class="plan-tile__period">/ anno</span></p>
                <button type="button" class="back-link plan-tile__more">Leggi di più <b-icon icon="chevron-right"></b-icon></button>
                <div class="plan-tile__actions"><b-button size="sm" variant="outline-secondary" block>Contattaci</b-button></div>
              </div>
            </div>

            <h3 class="section-eyebrow mt-4 mb-3">Card dei piani (PlanSheet)</h3>
            <p class="ds-note">
              Nome, prezzo, righe ed etichetta arrivano dai dati del piano: le condizioni
              calcolate dall'API dai limiti (qui compaiono solo i vantaggi rispetto al prezzo
              generale) e il dettaglio scritto dal listino. Le formule dello stesso piano
              stanno in una card sola; l'anello segna il piano della persona.
            </p>
            <div class="plan-sheets">
              <PlanSheet v-for="(gruppo, i) in pianiEsempio" :key="i" :piani="gruppo" :slug-corrente="i === 2 ? gruppo[0].slug : null">
                <template v-if="i < 2" #azioni>
                  <b-button variant="outline-secondary" block>Acquista</b-button>
                </template>
              </PlanSheet>
            </div>
          </div>
        </section>

        <!-- FORMA -->
        <!-- PROFILO SANITARIO -->
        <section id="profilo">
          <h2>Profilo sanitario</h2>
          <p class="ds-note">
            Le sezioni interne del profilo sanitario (e i pannelli della scheda paziente)
            sono righe di lista, non card: la riga scelta ha l'anello, le voci chiuse sono
            tenui, il dettaglio si apre sotto alla riga. I gruppi si aprono con un bottone
            in maiuscoletto; i riepiloghi sono coppie chiave/valore.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h3 class="section-eyebrow mb-3">Righe con stato, azioni e dettaglio</h3>
            <div class="list-stack mb-4">
              <div>
                <div class="list-row point list-row--active">
                  <span class="list-row__icon" aria-hidden="true"><b-icon icon="calendar4"></b-icon></span>
                  <div class="list-row__body">
                    <div class="list-row__title">Ipertensione nefrovascolare <b-badge variant="primary" pill class="text-uppercase">Primaria</b-badge></div>
                    <div class="list-row__meta">Cronica · dal 12/03/2021</div>
                  </div>
                  <div class="list-row__actions">
                    <b-button variant="white" class="btn-icon btn-sm" aria-label="Vedi"><b-icon icon="chevron-right"></b-icon></b-button>
                  </div>
                </div>
                <div class="list-row-detail d-flex justify-content-end">
                  <b-button size="sm" variant="secondary">Crea terapia per Ipertensione nefrovascolare</b-button>
                </div>
              </div>
              <div class="list-row point">
                <span class="list-row__icon" aria-hidden="true"><b-icon icon="watch"></b-icon></span>
                <div class="list-row__body">
                  <div class="list-row__title">RAMIPRIL 5MG</div>
                  <div class="list-row__meta">Cronica · Ipertensione nefrovascolare · mattina</div>
                </div>
                <span class="list-row__aside">Dal 06/11/2025</span>
                <div class="list-row__actions">
                  <b-button variant="white" class="btn-icon btn-sm" aria-label="Elimina"><b-icon icon="trash"></b-icon></b-button>
                  <b-icon icon="chevron-right" class="list-row__chevron" aria-hidden="true"></b-icon>
                </div>
              </div>
              <div class="list-row point list-row--muted">
                <span class="list-row__icon" aria-hidden="true"><b-icon icon="compass"></b-icon></span>
                <div class="list-row__body">
                  <div class="list-row__title">TACHIPIRINA 1000MG</div>
                  <div class="list-row__meta">Occasionale · sera</div>
                </div>
                <span class="list-row__aside">Terminata il 03/02/2026</span>
                <div class="list-row__actions">
                  <b-icon icon="chevron-right" class="list-row__chevron" aria-hidden="true"></b-icon>
                </div>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Gruppo apribile e chiave/valore</h3>
            <button type="button" class="group-toggle not-collapsed"><b-icon icon="chevron-down"></b-icon> Piani attivi</button>
            <div class="list-stack mb-4">
              <div>
                <div class="list-row">
                  <span class="list-row__icon" aria-hidden="true"><b-icon icon="journal-medical"></b-icon></span>
                  <div class="list-row__body">
                    <div class="list-row__title">Piano terapeutico <strong>#PT-2026-014</strong></div>
                    <div class="list-row__meta">Valido dal 01/02/2026 al 31/07/2026</div>
                  </div>
                  <div class="list-row__actions">
                    <b-button variant="white" class="btn-icon btn-sm" aria-label="Vedi"><b-icon icon="eye"></b-icon></b-button>
                    <b-button variant="white" class="btn-icon btn-sm" aria-label="Dettagli"><b-icon icon="chevron-down"></b-icon></b-button>
                  </div>
                </div>
                <div class="list-row-detail">
                  <ul class="kv-list">
                    <li class="kv-list__row"><span class="kv-list__key">Etichetta</span><span class="kv-list__value">Consegna mensile</span></li>
                    <li class="kv-list__row"><span class="kv-list__key">Contenuto</span><span class="kv-list__value">Ramipril 5mg × 2</span></li>
                  </ul>
                </div>
              </div>
            </div>

            <h3 class="section-eyebrow mb-3">Dispositivo</h3>
            <div class="list-row">
              <span class="device-thumb" aria-hidden="true"></span>
              <div class="list-row__body">
                <div class="list-row__title">BioBeat Chest Monitor – PUG-0002-02</div>
                <div class="list-row__meta"># 78314</div>
                <div class="list-row__meta"><span class="mr-3"><span class="status-dot" aria-hidden="true"></span>Frequenza cardiaca</span><span class="mr-3"><span class="status-dot" aria-hidden="true"></span>Saturazione</span><span><span class="status-dot status-dot--off" aria-hidden="true"></span>EWS</span></div>
              </div>
              <div class="list-row__actions">
                <b-button size="sm" variant="outline-secondary">Richiedi aggiornamento dati</b-button>
                <b-button variant="white" class="btn-icon btn-sm" aria-label="Elimina"><b-icon icon="trash"></b-icon></b-button>
                <b-button variant="white" class="btn-icon btn-sm" aria-label="Modifica"><b-icon icon="pencil"></b-icon></b-button>
              </div>
            </div>
          </div>

          <div class="shadowedCard p-4">
            <h3 class="section-eyebrow mb-3">Card del piano attivo e tessere "Cosa prevede"</h3>
            <b-row>
              <b-col class="col-12 col-md-5 d-flex flex-column mb-4 mb-md-0">
                <p class="section-eyebrow mb-2">Piano attivo <b-icon icon="check-circle-fill" variant="success"></b-icon></p>
                <b-card title="ConCura SMART" class="plan-card flex-grow-1" text-variant="white">
                  <p class="plan-card__price"><span class="figure">60€</span><span class="plan-card__period">/ anno</span></p>
                  <p class="plan-card__row"><span>Durata minima:</span><span>1 anno</span></p>
                  <p class="plan-card__row"><span>Si rinnova il</span><span>20/02/2027</span></p>
                  <div class="plan-card__actions">
                    <span class="fw-bolder text-underlined point">Leggi di più</span>
                    <b-button variant="white" class="btn-icon btn-sm" aria-label="Modifica"><b-icon icon="pencil"></b-icon></b-button>
                  </div>
                </b-card>
              </b-col>
              <b-col class="col-12 col-md-7">
                <p class="section-eyebrow mb-2">Cosa prevede la tua sottoscrizione</p>
                <div class="stat-grid stat-grid--two">
                  <div class="stat-tile"><div class="stat-tile__value">12</div><div class="stat-tile__label">Prestazioni disponibili</div></div>
                  <div class="stat-tile"><div class="stat-tile__value">6</div><div class="stat-tile__label">Ordini farmaci disponibili</div></div>
                  <div class="stat-tile"><div class="stat-tile__value">0</div><div class="stat-tile__label">Teleconsulto rapido</div></div>
                  <div class="stat-tile"><div class="stat-tile__value">0</div><div class="stat-tile__label">Teleconsulto pediatrico</div></div>
                </div>
              </b-col>
            </b-row>
          </div>
        </section>

        <!-- AREA OPERATORE: CRUSCOTTI, NEWS2, TIMELINE -->
        <section id="operatore">
          <h2>Cruscotti dell'operatore</h2>
          <p class="ds-note">
            Nei cruscotti i numeri stanno in una riga di tessere, i grafici in card
            uguali (la tela di chart.js dentro un riquadro che non la fa uscire) e una
            sola percentuale d'insieme può diventare un anello. Nella scheda paziente
            il punteggio NEWS2 e la timeline clinica hanno una pelle propria
            (<code>_ews.scss</code>, <code>_timeline.scss</code>) fatta di soli token.
          </p>
          <div class="shadowedCard p-4 mb-4">
            <h3 class="section-eyebrow mb-3">Riga di numeri e card dei grafici</h3>
            <div class="stat-grid stat-grid--four mb-4">
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--mild"><b-icon icon="people"></b-icon></div>
                <div class="stat-tile__value">1.284</div>
                <div class="stat-tile__label">Pazienti</div>
              </div>
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--success"><b-icon icon="journal-medical"></b-icon></div>
                <div class="stat-tile__value">312</div>
                <div class="stat-tile__label">Terapie inserite</div>
              </div>
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--danger"><b-icon icon="cart2"></b-icon></div>
                <div class="stat-tile__value">96</div>
                <div class="stat-tile__label">Ordini gestiti</div>
              </div>
              <div class="stat-tile">
                <div class="stat-tile__icon stat-tile__icon--warning"><b-icon icon="layout-text-sidebar-reverse"></b-icon></div>
                <div class="stat-tile__value">18</div>
                <div class="stat-tile__label">Servizi erogati</div>
              </div>
            </div>
            <!-- A coppie: una card rimasta sola sulla riga prende tutta la larghezza -->
            <div class="chart-grid">
              <div>
                <div class="chart-card">
                  <h2 class="chart-card__title">Andamento ordini</h2>
                  <div class="chart-card__box d-flex align-items-end" style="gap: 0.5rem">
                    <div class="w-100 bg-secondary" style="height: 30%; border-radius: 6px"></div>
                    <div class="w-100 bg-secondary" style="height: 55%; border-radius: 6px"></div>
                    <div class="w-100 bg-secondary" style="height: 42%; border-radius: 6px"></div>
                    <div class="w-100 bg-secondary" style="height: 78%; border-radius: 6px"></div>
                    <div class="w-100 bg-secondary" style="height: 61%; border-radius: 6px"></div>
                  </div>
                </div>
              </div>
              <div>
                <div class="chart-card">
                  <h2 class="chart-card__title">Avanzamento dello studio</h2>
                  <div class="ring-stat">
                    <span class="ring-stat__value">64%</span>
                    <span class="ring-stat__label">Pazienti con iter concluso</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="shadowedCard p-4">
            <h3 class="section-eyebrow mb-3">Punteggio NEWS2 e timeline clinica</h3>
            <div class="ews-indicator mb-4">
              <span class="badge ews-badge badge-success-custom">EWS 1 · 20/08 18:17</span>
              <span class="badge ews-badge badge-warning-custom">EWS 4 · 20/08 18:17</span>
              <span class="badge ews-badge badge-orange-custom">EWS 6 · 20/08 18:17</span>
              <span class="badge ews-badge badge-danger-custom">EWS 9 · 20/08 18:17</span>
              <span class="badge ews-badge badge-stale">EWS 3 · obsoleta da 4 giorni</span>
            </div>
            <div class="filter-chips mb-3">
              <button type="button" class="filter-chip filter-chip--active">
                <span class="aisp-cat-dot" style="background-color: #009DA9"></span> Rilevazioni
              </button>
              <button type="button" class="filter-chip">
                <span class="aisp-cat-dot" style="background-color: #F2A93B"></span> Terapie
              </button>
              <button type="button" class="filter-chip">
                <span class="aisp-cat-dot" style="background-color: #E4572E"></span> Alert
              </button>
            </div>
            <div class="list-stack">
              <div class="list-row">
                <span class="aisp-event-dot mr-2" style="background-color: #E4572E"></span>
                <div class="list-row__body">
                  <div class="list-row__title">Pressione sistolica fuori soglia</div>
                  <div class="list-row__meta">Valore 165 mmHg · max 140</div>
                </div>
                <span class="list-row__aside">20/08/2026 18:17</span>
              </div>
              <div class="list-row">
                <span class="aisp-event-dot mr-2" style="background-color: #009DA9"></span>
                <div class="list-row__body">
                  <div class="list-row__title">Saturazione registrata</div>
                  <div class="list-row__meta">96%</div>
                </div>
                <span class="list-row__aside">19/08/2026 09:02</span>
              </div>
            </div>
          </div>
        </section>

        <section id="forma">
          <h2>Forma</h2>
          <p class="ds-note">
            I raggi vengono letti dal tema compilato. I tab usano un raggio diverso dalla
            base: è un'incoerenza residua da sanare.
          </p>
          <div class="shadowedCard p-4 ds-row">
            <div class="ds-radius" ref="radiusBase">
              <div class="ds-radius__box"></div><span>base · {{ radius }}</span>
            </div>
            <div class="ds-radius">
              <div class="ds-radius__box ds-radius__box--control"></div><span>controlli · 12px</span>
            </div>
            <div class="ds-radius">
              <div class="ds-radius__box ds-radius__box--modal"></div><span>modali · 24px</span>
            </div>
            <div class="ds-radius">
              <div class="ds-radius__box ds-radius__box--pill"></div><span>bottoni · pillola</span>
            </div>
          </div>
        </section>
      </main>
    </div>

    <footer class="ds-foot">
      <div class="ds-wrap">
        <p class="mb-0">
          Pagina di riferimento del design system. Generata dal tema attivo:
          non contiene colori propri, legge tutto dalle variabili compilate.
        </p>
      </div>
    </footer>
  </div>
</template>

<script>
import PlanSheet from "@/app/modules/sottoscrizioni/components/PlanSheet.vue";

// Condizioni di esempio nella forma dell'API: incluse all'anno, prezzo, prezzo generale
const condizione = (incluse, prezzo, prezzoGenerale) => ({ incluse, prezzo, prezzo_generale: prezzoGenerale });
const servizio = (chiave, valore) => "<li><strong>" + chiave + ":</strong> " + valore + "</li>";

export default {
  name: "DesignSystem",
  components: { PlanSheet },
  data() {
    return {
      showModal: false,
      pianiEsempio: [
        [
          {
            slug: "esempio-easy-plus",
            nome: "ConCura EASY+",
            prezzo: 59,
            mesi_frequenza_rinnovo: 12,
            descrizione: "La soluzione ideale per la gestione quotidiana di visite e terapie.",
            dettaglio: '<p class="piano-etichetta">Più scelto</p><ul><li>Convenzioni ConCura e supporto telefonico operatore</li><li>Archiviazione referti</li></ul>',
            condizioni: {
              prestazione: condizione(12, 5, 10),
              ordine: condizione(6, 3, 4),
              teleconsulto: condizione(0, 10, 10),
              emergenza: condizione(0, 60, 70),
              tc_pediatrico: condizione(0, 60, 70),
            },
          },
        ],
        ["mensile", "annuale"].map((formula) => ({
          slug: "esempio-primary-care-" + formula,
          nome: "Primary Care",
          prezzo: formula === "mensile" ? 40 : 480,
          mesi_frequenza_rinnovo: formula === "mensile" ? 1 : 12,
          descrizione: "La formula di gestione continuativa con il supporto costante del team clinico e dell'Health Assistant.",
          dettaglio:
            '<p class="piano-etichetta">Continuità</p><ul>' +
            servizio("Accesso al team clinico", "Health Assistant dedicato") +
            servizio("Coordinamento prenotazioni", "entro 24/48h") +
            servizio("Team clinico dedicato", "chat illimitata (risposta entro 4 ore lavorative)") +
            servizio("Contatti da Health Assistant", "2 all'anno") +
            '</ul><p class="piano-nota"><strong>Estensioni per il nucleo familiare:</strong> coniuge −40%, figli −50%</p>',
          condizioni: {
            prestazione: condizione(0, 10, 10),
            ordine: condizione(0, 4, 4),
            teleconsulto: condizione(0, 10, 10),
            emergenza: condizione(0, 70, 70),
            tc_pediatrico: condizione(0, 70, 70),
          },
        })),
        [
          {
            slug: "esempio-exclusive-care",
            nome: "Exclusive Care",
            prezzo: 125,
            mesi_frequenza_rinnovo: 1,
            descrizione: "La presa in carico completa con colloquio iniziale medico, teleconsulti rapidi e prima visita inclusa.",
            dettaglio:
              '<p class="piano-etichetta">Più completo</p><ul>' +
              '<li class="piano-in-testa"><strong>Colloquio iniziale con il medico:</strong> circa 30 min incluso</li>' +
              servizio("Teleconsulto con il medico", "incluso entro 48h + 1 entro 60 min") +
              "</ul>",
            condizioni: {
              prestazione: condizione(0, 10, 10),
              ordine: condizione(0, 4, 4),
              teleconsulto: condizione(0, 0, 10),
              emergenza: condizione(0, 0, 70),
              tc_pediatrico: condizione(0, 70, 70),
            },
          },
        ],
      ],
      weights: [200, 300, 400, 500, 600, 700, 800],
      primitives: [
        { v: "brand-teal", role: "marchio" },
        { v: "brand-teal-deep", role: "teal leggibile" },
        { v: "brand-blue", role: "azione primaria" },
        { v: "brand-blue-deep", role: "fondo scuro" },
        { v: "n-0", role: "superficie" },
        { v: "n-50", role: "fondo pagina" },
        { v: "n-100", role: "superficie alt." },
        { v: "n-200", role: "separatori" },
        { v: "n-400", role: "bordi, icone off" },
        { v: "n-500", role: "testo secondario" },
        { v: "n-700", role: "testo corrente" },
        { v: "n-900", role: "titoli" },
        { v: "s-success", role: "esito positivo" },
        { v: "s-warning", role: "attenzione" },
        { v: "s-danger", role: "errore" },
        { v: "s-info", role: "informazione" },
        { v: "sev-high", role: "severità alta" },
        { v: "sev-medium", role: "severità media" },
        { v: "sev-low", role: "severità bassa" },
        { v: "sev-none", role: "nessuna severità" },
      ],
      resolved: {},
      contrast: {},
      fontFamily: "",
      radius: "",
      dsServizi: [
        { value: null, text: "Scegli un servizio" },
        "Teleconsulto",
        "Monitoraggio pressorio",
        "Rilevazione parametri",
      ],
      dsServizio: null,
      dsData: "",
      dsConsensi: { privacy: true, promemoria: false },
      dsModalita: "In presenza",
      dsSwitch: true,
      dsTags: ["ipertensione", "diabete"],
      dsTagsStatic: ["ipertensione", "diabete", "follow-up"],
      dsPage: 1,
      dsPeriodo: "settimana",
      serviceIcons: [
        "visita", "farmaci", "teleconsulto", "teleconsulto-rapido", "pediatrico",
        "monitoraggio", "questionario", "referti", "agenda", "appuntamento",
        "circolo-di-cura", "familiari", "profilo-sanitario", "sottoscrizione",
        "supporto", "notifiche", "statistiche", "paziente", "terapia", "chat",
      ],
      dsDay: "2026-09-07",
      signalVariants: ["secondary", "primary", "success", "danger", "warning", "info"],
      severity: [
        { token: "severity-none", label: "Nessuna" },
        { token: "severity-1", label: "Bassa" },
        { token: "severity-2", label: "Media" },
        { token: "severity-3", label: "Alta" },
      ],
      tableFields: [
        { key: "paziente", label: "Paziente" },
        { key: "servizio", label: "Servizio" },
        { key: "data", label: "Data" },
        { key: "stato", label: "Stato" },
      ],
      tableItems: [
        { paziente: "M. Rossi", servizio: "Teleconsulto", data: "12/09/2026", stato: "Confermata" },
        { paziente: "A. Bianchi", servizio: "Monitoraggio pressorio", data: "13/09/2026", stato: "In attesa" },
        { paziente: "L. Verdi", servizio: "Rilevazione parametri", data: "15/09/2026", stato: "Confermata" },
        { paziente: "G. Neri", servizio: "Holter pressorio", data: "16/09/2026", stato: "Da valutare" },
        { paziente: "S. Conti", servizio: "Teleconsulto", data: "18/09/2026", stato: "In attesa" },
        { paziente: "P. Ferri", servizio: "Rilevazione parametri", data: "19/09/2026", stato: "Confermata" },
      ],
      sections: [
        { id: "primitive", label: "Primitive" },
        { id: "glass", label: "Superfici vetro" },
        { id: "tipografia", label: "Tipografia" },
        { id: "bottoni", label: "Bottoni" },
        { id: "form", label: "Form e input" },
        { id: "contenitori", label: "Superfici" },
        { id: "segnali", label: "Badge e tag" },
        { id: "severita", label: "Severità clinica" },
        { id: "navigazione", label: "Navigazione" },
        { id: "sovrapposti", label: "Sovrapposti" },
        { id: "dati", label: "Tabelle e attese" },
        { id: "icone", label: "Icone" },
        { id: "pagine", label: "Pattern di pagina" },
        { id: "profilo", label: "Profilo sanitario" },
        { id: "operatore", label: "Cruscotti operatore" },
        { id: "forma", label: "Forma" },
      ],
    };
  },
  computed: {
    scope() {
      return process.env.VUE_APP_SCOPE || "concura";
    },
    tokenCount() {
      return this.primitives.length;
    },
  },
  mounted() {
    this.$nextTick(this.readTheme);
  },
  methods: {
    showToast() {
      this.$bvToast.toast("Referto salvato. Lo trovi nell'archivio del paziente.", {
        title: "Salvato",
        variant: "success",
        solid: true,
        toaster: "b-toaster-bottom-right",
      });
    },
    readTheme() {
      const body = getComputedStyle(document.body);
      this.fontFamily = (body.fontFamily || "").split(",")[0].replace(/["']/g, "");

      const base = this.$refs.radiusBase && this.$refs.radiusBase.querySelector(".ds-radius__box");
      this.radius = base ? getComputedStyle(base).borderTopLeftRadius : "";

      const bg = this.toRgb(body.backgroundColor) || [249, 249, 249];
      const out = {};
      const cr = {};
      document.querySelectorAll("[data-token]").forEach((el) => {
        const name = el.dataset.token;
        const c = getComputedStyle(el).backgroundColor;
        out[name] = this.pretty(c);
        const rgb = this.toRgb(c);
        cr[name] = rgb && rgb[3] >= 0.999 ? this.ratio(rgb, bg) : null;
      });
      this.resolved = out;
      this.contrast = cr;
    },
    toRgb(v) {
      const m = /rgba?\(([^)]+)\)/.exec(v || "");
      if (!m) return null;
      const p = m[1].split(",").map((x) => parseFloat(x));
      return [p[0], p[1], p[2], p.length > 3 ? p[3] : 1];
    },
    pretty(v) {
      const c = this.toRgb(v);
      if (!c) return v;
      if (c[3] < 0.999) return v;
      const h = (n) => Math.round(n).toString(16).padStart(2, "0");
      return "#" + h(c[0]) + h(c[1]) + h(c[2]);
    },
    lum(c) {
      const f = (x) => {
        x /= 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
      };
      return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
    },
    ratio(a, b) {
      const l1 = this.lum(a);
      const l2 = this.lum(b);
      const hi = Math.max(l1, l2);
      const lo = Math.min(l1, l2);
      return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
    },
    crLabel(t) {
      const c = this.contrast[t];
      if (c === null || c === undefined) return "trasparente";
      return c + ":1";
    },
    crClass(t) {
      const c = this.contrast[t];
      if (c === null || c === undefined) return "text-muted";
      if (c >= 4.5) return "text-success";
      if (c >= 3) return "text-orange";
      return "text-danger";
    },
  },
};
</script>
